'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

export type PaginatedResponse<T> = {
  data: T[];
  total: number;
  hasMore: boolean;
};

export type PaginationState<T> = {
  data: T[];
  isInitializing: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  total: number;
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;
  replace: (keyword?: string) => Promise<void>;
  /** Directly update the data array (e.g. prepend a newly created item). */
  updateData: (updater: (data: T[]) => T[]) => void;
};

type LoadDataFn<T> = (
  page: number,
  pageSize: number,
  keyword?: string,
) => Promise<PaginatedResponse<T>>;

type Options = {
  pageSize?: number;
  autoLoad?: boolean;
};

/**
 * Generic async pagination hook.
 * Ported from PopshopRN's useAsyncPagination pattern.
 */
export function useAsyncPagination<T>(
  loadData: LoadDataFn<T>,
  options?: Options,
): PaginationState<T> {
  const pageSize = options?.pageSize ?? 25;
  const autoLoad = options?.autoLoad ?? true;

  const [data, setData] = useState<T[]>([]);
  const [isInitializing, setIsInitializing] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [total, setTotal] = useState(0);

  const pageRef = useRef(1);
  const keywordRef = useRef<string | undefined>(undefined);
  const initIdRef = useRef(0);
  const isLoadingRef = useRef(false);

  const load = useCallback(
    async (page: number, keyword: string | undefined, initId: number) => {
      if (isLoadingRef.current) return;
      isLoadingRef.current = true;

      const isFirstPage = page === 1;

      if (isFirstPage) {
        setIsInitializing(true);
      } else {
        setIsLoadingMore(true);
      }

      try {
        const result = await loadData(page, pageSize, keyword);

        // Guard against stale responses after re-init
        if (initIdRef.current !== initId) return;

        setData(prev =>
          isFirstPage ? result.data : [...prev, ...result.data],
        );
        setTotal(result.total);
        setHasMore(result.hasMore);
        pageRef.current = page;
      } catch (error) {
        if (initIdRef.current !== initId) return;
        console.error('[useAsyncPagination] load error:', error);
      } finally {
        if (initIdRef.current === initId) {
          setIsInitializing(false);
          setIsLoadingMore(false);
          isLoadingRef.current = false;
        }
      }
    },
    [loadData, pageSize],
  );

  const loadMore = useCallback(async () => {
    if (!hasMore || isLoadingRef.current) return;
    await load(pageRef.current + 1, keywordRef.current, initIdRef.current);
  }, [hasMore, load]);

  const refresh = useCallback(async () => {
    const newInitId = ++initIdRef.current;
    isLoadingRef.current = false;
    pageRef.current = 1;
    setData([]);
    await load(1, keywordRef.current, newInitId);
  }, [load]);

  const replace = useCallback(
    async (keyword?: string) => {
      keywordRef.current = keyword;
      const newInitId = ++initIdRef.current;
      isLoadingRef.current = false;
      pageRef.current = 1;
      setData([]);
      await load(1, keyword, newInitId);
    },
    [load],
  );

  // Auto-load on mount (and when load identity changes)
  const autoLoadRef = useRef(autoLoad);
  useEffect(() => {
    if (autoLoadRef.current) {
      const initId = ++initIdRef.current;
      isLoadingRef.current = false;
      void load(1, undefined, initId);
    }
  }, [load]);

  const updateData = useCallback((updater: (data: T[]) => T[]) => {
    setData(prev => updater(prev));
  }, []);

  return {
    data,
    isInitializing,
    isLoadingMore,
    hasMore,
    total,
    loadMore,
    refresh,
    replace,
    updateData,
  };
}
